import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import collabCheckListApi from "../../../api/collabCheckListApi";
import collaborationApi from "../../../api/collaborationApi";
import checklistItems from "./checklistItems";

const CollaborationChecklist = ({ collabId, collaborationName }) => {
  const [collabDetails, setCollabDetails] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [editingId, setEditingId] = useState(null);

  // Dropdown items from checklistItems.js
  const [dropdownItems] = useState(checklistItems);

  // Controls whether custom "Other" input is visible
  const [showCustomItem, setShowCustomItem] = useState(false);

  // Used for calculating time taken
  const [formOpenedAt, setFormOpenedAt] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const senderEmail = user?.userEmail;

  const [formData, setFormData] = useState({
    item: "",
    assignedTo: "",
    role: "",
    email: "",
    comment: "",
    status: false,
    deadline: "",
    collabName: collaborationName || "",
    addedBy: senderEmail,
    timeTakenSeconds: null,
  });

  console.log("collabId:", collabId);

  // =========================================================
  // FETCH COLLABORATION DETAILS
  // =========================================================
  useEffect(() => {
    const fetchCollabBrief = async () => {
      if (!collabId) return;

      try {
        const response = await collaborationApi.getCollaBriefById(collabId);

        setCollabDetails(response.data);
      } catch (err) {
        console.error("Error fetching collab brief:", err);
      }
    };

    fetchCollabBrief();
  }, [collabId]);

  // =========================================================
  // FETCH CHECKLIST
  // =========================================================
  useEffect(() => {
    console.log("collabId:", collabId);

    if (collabId) {
      fetchChecklist();
    }
  }, [collabId]);

  const fetchChecklist = async () => {
    console.log("fetchChecklist called");

    try {
      const response =
        await collabCheckListApi.getChecklistByCollabId(collabId);

      console.log("Checklist Response:", response);
      console.log("Checklist Data:", response.data);

      if (response.status === 200) {
        setChecklist(response.data);
      }
    } catch (error) {
      console.error("Error fetching checklist:", error);
      console.error("Response:", error.response);
    }
  };

  // =========================================================
  // FETCH CONTRIBUTORS
  // =========================================================
  useEffect(() => {
    if (collabId) {
      collaborationApi
        .getContributorsDetails(collabId)
        .then((response) => {
          const contributorsData = response.data.map((item) => ({
            id: item[0],
            name: item[1],
            email: item[2],
            role: item[3],
          }));

          setContributors(contributorsData);
        })
        .catch((err) => {
          console.error("Error fetching contributors:", err);
        });
    }
  }, [collabId]);

  // =========================================================
  // HANDLE FORM CHANGE
  // =========================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // =========================================================
  // OPEN ADD FORM
  // =========================================================
  const handleOpenAddForm = () => {
    setFormOpenedAt(Date.now());

    setEditingId(null);

    setShowCustomItem(false);

    setFormData({
      item: "",
      assignedTo: "",
      role: "",
      email: "",
      comment: "",
      status: false,
      deadline: "",
      collabName: collaborationName || "",
      addedBy: senderEmail,
      timeTakenSeconds: null,
    });

    setShowForm(true);
  };

  // =========================================================
  // HANDLE CHECKLIST ITEM DROPDOWN
  // =========================================================
  const handleItemChange = (e) => {
    const selectedItem = e.target.value;

    if (selectedItem === "Other") {
      // Show manual input.
      // IMPORTANT:
      // We do NOT store "Other" in formData.item.
      // item will contain the manually entered value.
      setShowCustomItem(true);

      setFormData((prev) => ({
        ...prev,
        item: "",
      }));
    } else {
      // Normal checklist item
      setShowCustomItem(false);

      setFormData((prev) => ({
        ...prev,
        item: selectedItem,
      }));
    }
  };

  // =========================================================
  // HANDLE ADD / UPDATE
  // =========================================================
  const handleAdd = async (e) => {
    e.preventDefault();

    const errors = [];

    // Validate item
    if (!formData.item || !formData.item.trim()) {
      errors.push("Checklist Item is required");
    }

    // Validate deadline
    if (!formData.deadline) {
      errors.push("Deadline is required");
    }

    // Validate assigned role
    if (!formData.assignedTo) {
      errors.push("Assign To (Actor/Reviewer) is required");
    }

    // Validate email
    if (!formData.email) {
      errors.push("Contributor Email is required");
    }

    // Show validation errors
    if (errors.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        html: `
          <ul style="text-align:left;">
            ${errors.map((err) => `<li>${err}</li>`).join("")}
          </ul>
        `,
      });

      return;
    }

    // =====================================================
    // CALCULATE TIME TAKEN
    // =====================================================
    const timeTakenSeconds =
      formData.status && formOpenedAt
        ? Math.round((Date.now() - formOpenedAt) / 1000)
        : null;

    // Remember whether this is update before setEditingId(null)
    const isEditing = Boolean(editingId);

    // =====================================================
    // REQUEST BODY
    // =====================================================
    const newItem = {
      ...formData,

      collabId,
      collabName: collaborationName,
      addedBy: senderEmail,

      // Backend expects List<String>
      email: [formData.email],

      // Time taken in seconds
      timeTakenSeconds,
    };

    console.log("Checklist request:", newItem);

    try {
      // =====================================================
      // UPDATE EXISTING CHECKLIST
      // =====================================================
      if (editingId) {
        await collabCheckListApi.updateChecklist(editingId, newItem);

        setChecklist((prev) =>
          prev.map((c) =>
            c.id === editingId
              ? {
                  ...c,
                  ...newItem,
                  timeTakenSeconds,
                }
              : c,
          ),
        );

        setEditingId(null);
      } else {
        // ===================================================
        // ADD NEW CHECKLIST
        // ===================================================
        const response = await collabCheckListApi.addChecklist(newItem);

        console.log("Save checklist response:", response);

        setChecklist((prev) => [
          ...prev,
          {
            ...response.data,
            timeTakenSeconds,
          },
        ]);
      }

      // Close form
      setShowForm(false);

      // Reset timer
      setFormOpenedAt(null);

      // Reset custom item state
      setShowCustomItem(false);

      Swal.fire({
        icon: "success",
        title: isEditing ? "Checklist Updated" : "Checklist Saved",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Save checklist error:", error);

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          error?.response?.data?.message ||
          "Unable to save checklist",
      });
    }
  };

  // =========================================================
  // DELETE CHECKLIST
  // =========================================================
  const handleRemove = (id) => {
    collabCheckListApi
      .updateStatus(id)
      .then((response) => {
        if (response.status === 200) {
          setChecklist((prev) =>
            prev.map((c) =>
              c.id === id
                ? {
                    ...c,
                    status: false,
                  }
                : c,
            ),
          );

          Swal.fire({
            icon: "success",
            title: "Deleted Successfully",
            text: "The checklist item has been deleted successfully.",
            confirmButtonText: "OK",
          });

          fetchChecklist();
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text:
            "Something went wrong while deleting the checklist item.",
          confirmButtonText: "OK",
        });

        console.error("Error deleting checklist:", error);
      });
  };

  // =========================================================
  // FILTER CONTRIBUTORS BY ROLE
  // =========================================================
  const getFilteredEmails = (role) => {
    return contributors
      .filter((user) => user.role === role)
      .map((user) => user.email);
  };

  // =========================================================
  // EDIT CHECKLIST
  // =========================================================
  const handleEdit = (item) => {
    /*
     * If the existing item is present in the standard dropdown,
     * show the dropdown normally.
     *
     * If it is NOT present in the dropdown, it is a custom item.
     * In that case show the "Other" manual input.
     */

    const isCustomItem = !dropdownItems.includes(item.item);

    setShowCustomItem(isCustomItem);

    setFormData({
      item: item.item || "",
      assignedTo: item.assignedTo || "",
      role: item.assignedTo || "",
      email: Array.isArray(item.email)
        ? item.email[0] || ""
        : item.email || "",
      comment: item.comment || "",
      status: Boolean(item.status),
      deadline: item.deadline || "",
      collabName: collaborationName || "",
      addedBy: senderEmail,
      timeTakenSeconds: item.timeTakenSeconds ?? null,
    });

    setEditingId(item.id);

    // Start timer when edit form opens
    setFormOpenedAt(Date.now());

    setShowForm(true);
  };

  // =========================================================
  // FORMAT TIME
  // =========================================================
  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) {
      return "-";
    }

    const m = Math.floor(seconds / 60);
    const s = seconds % 60;

    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      <br />

      <h4 className="mb-2">
        <strong>CheckList for {collaborationName}</strong>
      </h4>

      <div className="d-flex align-items-center justify-content-between">
        <h5 className="mb-0">📄 Checklist</h5>

        <Button
          onClick={() => {
            if (!showForm) {
              handleOpenAddForm();
            } else {
              setShowForm(false);
              setFormOpenedAt(null);
              setShowCustomItem(false);
            }
          }}
        >
          {showForm ? "← Back to List" : "+ Add Checklist"}
        </Button>
      </div>

      <p>
        <strong>
          For each collaboration you select, if you send a checklist,
          0.5 frymes will be deducted.
        </strong>
      </p>

      {/* =====================================================
          CHECKLIST LIST
          ===================================================== */}
      {!showForm ? (
        <>
          {checklist.length > 0 ? (
            <div
              style={{
                overflowX: "auto",
                width: "100%",
              }}
            >
              <Table
                striped
                bordered
                hover
                responsive
                style={{
                  minWidth: "1100px",
                  whiteSpace: "nowrap",
                }}
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Checklist Item</th>
                    <th>Assigned To</th>
                    <th>Status</th>
                    <th>Deadline</th>
                    <th>Time Taken</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {checklist.map((c, index) => (
                    <tr key={c.id}>
                      <td>{index + 1}</td>

                      <td>{c.item}</td>

                      <td>
                        {Array.isArray(c.email)
                          ? c.email.join(", ")
                          : c.email}{" "}
                        - {c.assignedTo}
                      </td>

                      <td>
                        {c.status ? "Done" : "Not Done"}
                      </td>

                      <td>{c.deadline}</td>

                      <td>
                        {c.status
                          ? formatDuration(c.timeTakenSeconds)
                          : "-"}
                      </td>

                      <td>
                        {/* DELETE */}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`delete-tooltip-${c.id}`}>
                              Delete
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="me-2"
                            onClick={() => handleRemove(c.id)}
                          >
                            <FaTrash />
                          </Button>
                        </OverlayTrigger>

                        {/* EDIT */}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`edit-tooltip-${c.id}`}>
                              Edit
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleEdit(c)}
                          >
                            ✏️
                          </Button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-muted mb-0">
              No checklist added yet.
            </p>
          )}
        </>
      ) : (
        /* =====================================================
           ADD / EDIT FORM
           ===================================================== */
        <Form onSubmit={handleAdd}>
          {/* =================================================
              ITEM + DEADLINE
              ================================================= */}
          <Row>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                controlId="item"
              >
                <Form.Label>
                  Checklist Item
                  <span className="text-danger">*</span>
                </Form.Label>

                {!showCustomItem ? (
                  <>
                    <Form.Select
                      name="item"
                      value={formData.item}
                      onChange={handleItemChange}
                      disabled={!collabId}
                      required
                    >
                      <option value="">
                        Select...
                      </option>

                      {dropdownItems
                        .filter(
                          (item) => item !== "Other",
                        )
                        .map((item, index) => (
                          <option
                            key={index}
                            value={item}
                          >
                            {item}
                          </option>
                        ))}

                      <option value="Other">
                        Other
                      </option>
                    </Form.Select>
                  </>
                ) : (
                  <>
                    {/* Keep dropdown showing Other */}
                    <Form.Select
                      value="Other"
                      onChange={handleItemChange}
                      disabled={!collabId}
                    >
                      <option value="Other">
                        Other
                      </option>

                      {dropdownItems
                        .filter(
                          (item) => item !== "Other",
                        )
                        .map((item, index) => (
                          <option
                            key={index}
                            value={item}
                          >
                            {item}
                          </option>
                        ))}
                    </Form.Select>

                    {/* CUSTOM ITEM INPUT */}
                    <Form.Control
                      type="text"
                      name="item"
                      value={formData.item}
                      onChange={handleChange}
                      placeholder="Enter custom checklist item"
                      className="mt-2"
                      disabled={!collabId}
                      required
                    />
                  </>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group
                className="mb-3"
                controlId="deadline"
              >
                <Form.Label>
                  Deadline
                  <span className="text-danger">*</span>
                </Form.Label>

                <Form.Control
                  type="date"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleChange}
                  disabled={!collabId}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* =================================================
              ASSIGN TO + EMAIL
              ================================================= */}
          <Row>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                controlId="assignedTo"
              >
                <Form.Label>
                  Assign To
                  <span className="text-danger">*</span>
                </Form.Label>

                <Form.Select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={(e) => {
                    const selectedRole =
                      e.target.value;

                    setFormData((prev) => ({
                      ...prev,
                      assignedTo: selectedRole,
                      role: selectedRole,
                      email: "",
                    }));
                  }}
                  disabled={!collabId}
                  required
                >
                  <option value="">
                    Select a user...
                  </option>

                  <option value="Actor">
                    Actor
                  </option>

                  <option value="Reviewer">
                    Reviewer
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group
                className="mb-3"
                controlId="email"
              >
                <Form.Label>
                  Email
                  <span className="text-danger">*</span>
                </Form.Label>

                <Form.Select
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!collabId}
                  required
                >
                  <option value="">
                    Select Email
                  </option>

                  {getFilteredEmails(
                    formData.role,
                  ).map((email) => (
                    <option
                      key={email}
                      value={email}
                    >
                      {email}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* =================================================
              COMMENT
              ================================================= */}
          <Row>
            <Col md={12}>
              <Form.Group
                className="mb-3"
                controlId="comment"
              >
                <Form.Label>
                  Comment
                </Form.Label>

                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  disabled={!collabId}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* =================================================
              STATUS
              ================================================= */}
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Done"
                  name="status"
                  checked={formData.status}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.checked,
                    }));
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* =================================================
              SAVE / UPDATE
              ================================================= */}
          <Button
            variant="primary"
            type="submit"
          >
            {editingId
              ? "Update Checklist"
              : "Save Checklist"}
          </Button>
        </Form>
      )}
    </>
  );
};

export default CollaborationChecklist;